import React, { useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import Icon from '../../../icons/components/Icon';
import { IconArrowDown } from '../../../icons/svg';
const useStyles = makeStyles()(theme => {
    return {
        actionButton: {
            ...theme.typography.bodyLongBold,
            borderRadius: theme.shape.borderRadius,
            boxSizing: 'border-box',
            color: theme.palette.actionButtonText,
            cursor: 'pointer',
            display: 'inline-block',
            marginBottom: '16px',
            padding: '7px 16px',
            position: 'relative',
            textAlign: 'center',
            width: '100%',
            border: 0,
            '&.primary': {
                background: theme.palette.prejoinActionButtonPrimary,
                color: theme.palette.prejoinActionButtonPrimaryText,
                '&:hover': {
                    backgroundColor: theme.palette.prejoinActionButtonPrimaryHover
                }
            },
            '&.secondary': {
                background: theme.palette.prejoinActionButtonSecondary,
                color: theme.palette.prejoinActionButtonSecondaryText,
                '&:hover': {
                    backgroundColor: theme.palette.prejoinActionButtonSecondaryHover
                }
            },
            '&.text': {
                width: 'auto',
                fontSize: '0.875rem',
                margin: '0',
                padding: '0'
            },
            '&.disabled': {
                background: theme.palette.prejoinActionButtonDisabled,
                border: '1px solid #5E6D7A',
                color: '#AFB6BC',
                cursor: 'initial',
                '.icon': {
                    '& > svg': {
                        fill: '#AFB6BC'
                    }
                }
            },
            [theme.breakpoints.down(400)]: {
                fontSize: '1rem',
                marginBottom: 8,
                padding: '11px 16px'
            }
        },
        options: {
            borderRadius: Number(theme.shape.borderRadius) / 2,
            // The options area sits flush with the button's right edge, so its outer corners must follow the
            // button's radius, which can be customized through the theme.
            borderTopRightRadius: 'inherit',
            borderBottomRightRadius: 'inherit',
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            position: 'absolute',
            right: 0,
            top: 0,
            width: 36,
            '&:hover': {
                backgroundColor: theme.palette.prejoinActionButtonOptionsHover
            },
            '& svg': {
                pointerEvents: 'none'
            }
        }
    };
});
/**
 * Button used for pre meeting actions.
 *
 * @returns {ReactElement}
 */
function ActionButton({ children, className = '', disabled, hasOptions, OptionsIcon = IconArrowDown, testId, type = 'primary', onClick, onOptionsClick, tabIndex, role, ariaPressed, ariaLabel, ariaDropDownLabel }) {
    const { classes, cx } = useStyles();
    const onKeyPressHandler = useCallback(e => {
        if (onClick && !disabled && (e.key === ' ' || e.key === 'Enter')) {
            e.preventDefault();
            onClick(e);
        }
    }, [onClick, disabled]);
    const onOptionsKeyPressHandler = useCallback(e => {
        if (onOptionsClick && !disabled && (e.key === ' ' || e.key === 'Enter')) {
            e.preventDefault();
            e.stopPropagation();
            onOptionsClick(e);
        }
    }, [onOptionsClick, disabled]);
    const containerClasses = cx(classes.actionButton, className && className, type, disabled && 'disabled');
    return (React.createElement("div", { "aria-disabled": disabled, "aria-label": ariaLabel, className: containerClasses, "data-testid": testId ? testId : undefined, onClick: disabled ? undefined : onClick, onKeyPress: onKeyPressHandler, role: 'button', tabIndex: 0 },
        children,
        hasOptions
            && React.createElement("div", { "aria-disabled": disabled, "aria-haspopup": 'true', "aria-label": ariaDropDownLabel, "aria-pressed": ariaPressed, className: classes.options, "data-testid": 'prejoin.joinOptions', onClick: disabled ? undefined : onOptionsClick, onKeyPress: onOptionsKeyPressHandler, role: role, tabIndex: tabIndex },
                React.createElement(Icon, { className: 'icon', size: 24, src: OptionsIcon }))));
}
export default ActionButton;
